// /src/components/file-management/FileRow.tsx
import React from "react";
import { Stack, Typography } from "@mui/material";
import { InsertDriveFile } from "@mui/icons-material";
import styled, { createGlobalStyle } from "styled-components";
import DotsMenu from "../buttons/DotsMenu";

type MenuItem = { label: string; value: string | number; disabled?: boolean };
type Props = {
  title: string;
  subtitle?: string;
  animating?: boolean;
  disabled?: boolean;
  menuItems: MenuItem[];
  onMenuChange?: (value: string | number) => void;
};

const clamp = (n: number) => Math.max(0, Math.min(100, n ?? 0));

const Row = styled.div({
  position: "relative",
  border: "1px dashed rgba(0, 0, 0, 0.25)",
  borderRadius: 18,
  padding: "4px 14px",
  overflow: "hidden",
  background: "#f6fbf4",
});

/** 1) Inject global keyframes once. Use unique names to avoid clashes */
export const ProgressAnimations = createGlobalStyle`
@keyframes RowStripeMove {
  from { background-position: 0 0; }
  to   { background-position: 40px 0; }
}
@keyframes RowSheenSweep {
  0%   { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
}
`;

/** 2) Use the animation names (strings) in object styles */
export const RowProgress = styled.div<{ isUploading: boolean }>(
  ({ isUploading }) => {
    const animating = isUploading;

    return {
      position: "absolute",
      inset: 0,
      overflow: "hidden",
      borderRadius: "inherit",
      pointerEvents: "none",
      zIndex: 0,

      // width reflects progress
      width: `100%`,
      transition: "width 180ms ease-in-out",

      // soft base fill
      background:
        "linear-gradient(90deg, rgba(139,214,109,0.22), rgba(139,214,109,0.10))",

      // moving diagonal stripes
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        // backgroundImage:
        //   "repeating-linear-gradient(45deg, rgba(139,214,109,0.45) 0 10px, transparent 10px 20px)",
        backgroundSize: "40px 40px",
        opacity: animating ? 0.6 : 0,
        willChange: "background-position",
        animation: animating ? "RowStripeMove 900ms linear infinite" : "none",
      },

      // glossy sweep highlight
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        bottom: 0,
        width: "28%",
        transform: "translateX(-120%)",
        background:
          "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)",
        mixBlendMode: "overlay",
        willChange: "transform",
        animation: animating
          ? "RowSheenSweep 1.4s ease-in-out infinite"
          : "none",
      },

      "@media (prefers-reduced-motion: reduce)": {
        "&::before, &::after": { animation: "none" },
      },
    };
  }
);

const FileRow: React.FC<Props> = ({
  title,
  subtitle,
  disabled,
  menuItems,
  onMenuChange,
  animating = false,
}) => {
  return (
    <Row>
      {/* 3) Mount the global keyframes ONCE somewhere high (e.g., Files page root).
          Keeping it here works but may render multiple times if many rows are shown. */}
      <ProgressAnimations />

      <RowProgress isUploading={animating} />

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        position="relative"
        sx={{ zIndex: 1 }} // keep content above progress layer
      >
        <InsertDriveFile fontSize="small" />

        <Stack
          sx={{ flex: 1, minWidth: 0 }}
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <Typography
            variant="body2"
            fontWeight="medium"
            color="success"
            noWrap
            title={title}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant="caption"
              fontWeight="light"
              color="text.secondary"
              noWrap
              title={subtitle}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          {animating && (
            <Typography variant="caption">
              {animating ? "Uploading…" : "Done"}
            </Typography>
          )}
          <DotsMenu
            options={menuItems}
            disabled={disabled}
            onChange={(val: (string | number)[]) => onMenuChange?.(val[0])}
          />
        </Stack>
      </Stack>
    </Row>
  );
};

export default FileRow;
